import * as styles from "./Tabs.css";

import { clsx } from "clsx";
import {
  type JSX,
  useImperativeHandle,
  useLayoutEffect,
  useState,
} from "react";
import type { Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { useForwardSlottedEvents } from "@frontend/bridge/useForwardSlottedEvents";
import { Icon, type IconName } from "@frontend/components/atoms/Icon";
import {
  ScrollArea,
  ScrollAreaContent,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";

import { Tabs } from "./Tabs";
import { type TabsDataProps, tabsDataSchema } from "./Tabs.schema";
import { TabsContent } from "./TabsContent";
import { TabsIndicator } from "./TabsIndicator";
import { TabsList } from "./TabsList";
import { TabsPanel } from "./TabsPanel";
import { TabsTab } from "./TabsTab";
import {
  TABS_ACTIONS_TAG_NAME,
  TABS_CONTENT_TAG_NAME,
  TABS_LIST_TAG_NAME,
  TABS_PANEL_TAG_NAME,
  TABS_TAB_TAG_NAME,
  TABS_TAG_NAME,
} from "./constants";

export interface TabsHandle {
  setValue: (value: string) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [TABS_TAG_NAME]: { props: TabsDataProps; handle: TabsHandle };
  }
  interface InertTagNames {
    [TABS_ACTIONS_TAG_NAME]: true;
    [TABS_CONTENT_TAG_NAME]: true;
    [TABS_LIST_TAG_NAME]: true;
    [TABS_PANEL_TAG_NAME]: true;
    [TABS_TAB_TAG_NAME]: true;
  }
}

const RWCTabsActions = rwc(TABS_ACTIONS_TAG_NAME);

const RWCTabsContent = rwc(TABS_CONTENT_TAG_NAME);

const RWCTabsList = rwc(TABS_LIST_TAG_NAME);

const RWCTabsPanel = rwc(TABS_PANEL_TAG_NAME);

const RWCTabsTab = rwc(TABS_TAB_TAG_NAME);

const ACTIONS_SLOT = "tabs-actions";

export function readTabsSlotted(el: HTMLElement): Partial<TabsDataProps> {
  const tabs = Array.from(
    el.querySelectorAll<HTMLElement>(
      `:scope > ${RWCTabsList.tagName} > ${RWCTabsTab.tagName}`,
    ),
  ).map((tab) => ({
    value: tab.getAttribute("value") ?? "",
    label: tab.textContent?.trim() ?? "",
    icon: (tab.getAttribute("icon") ?? undefined) as IconName | undefined,
  }));

  el.querySelectorAll<HTMLElement>(`:scope > ${RWCTabsPanel.tagName}`).forEach(
    (panel) => {
      const value = panel.getAttribute("value");

      if (value) {
        panel.setAttribute("slot", value);
      }
    },
  );

  const actions = el.querySelector<HTMLElement>(
    `:scope > ${RWCTabsActions.tagName}`,
  );

  actions?.setAttribute("slot", ACTIONS_SLOT);

  return {
    tabs,
    hasActions: actions != null,
    onValueChange: (value) => {
      el.setAttribute("value", value);

      el.dispatchEvent(
        new CustomEvent("tabs:change", { detail: { value }, bubbles: true }),
      );
    },
  };
}

function RWCTabsComponent({
  ref,
  tabs = [],
  hasActions = false,
  defaultValue,
  value: controlledValue,
  onValueChange,
  alerts = [],
  scrollable = false,
  ...props
}: Partial<TabsDataProps> & { ref?: Ref<TabsHandle> }): JSX.Element {
  const [value, setValue] = useState(controlledValue ?? defaultValue);
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);

  useForwardSlottedEvents(viewport);

  useImperativeHandle(
    ref,
    () => ({
      setValue: (newValue) => {
        setValue(newValue);
        onValueChange?.(newValue);
      },
    }),

    [onValueChange],
  );

  useLayoutEffect(() => {
    if (defaultValue && controlledValue === undefined) {
      onValueChange?.(defaultValue);
    }
  }, []);

  useLayoutEffect(() => {
    if (controlledValue !== undefined) {
      setValue(controlledValue);
      onValueChange?.(controlledValue);
    }
  }, [controlledValue]);

  const handleValueChange = (newValue: string) => {
    setValue(newValue);
    onValueChange?.(newValue);
  };

  function renderTab(tab: (typeof tabs)[number]) {
    return (
      <TabsTab
        key={tab.value}
        value={tab.value}
        icon={tab.icon as IconName | undefined}
      >
        {tab.label}
        {alerts.includes(tab.value) ? (
          <Icon name="TriangleAlert" size="sm" className={styles.alert} />
        ) : null}
      </TabsTab>
    );
  }

  const panels = tabs.map((tab) => (
    <TabsPanel key={tab.value} value={tab.value}>
      <slot name={tab.value} />
    </TabsPanel>
  ));

  return (
    <Tabs
      value={value}
      onValueChange={handleValueChange}
      className={clsx(scrollable && styles.scrollable)}
      {...props}
    >
      {hasActions ? (
        <div className={styles.header}>
          <TabsList>
            {tabs.map(renderTab)}

            <TabsIndicator />
          </TabsList>

          <slot name={ACTIONS_SLOT} />
        </div>
      ) : (
        <TabsList>
          {tabs.map(renderTab)}

          <TabsIndicator />
        </TabsList>
      )}

      {scrollable ? (
        <TabsContent className={styles.scrollableContent}>
          <ScrollArea style={{ height: "100%" }}>
            <ScrollAreaViewport ref={setViewport} fade>
              <ScrollAreaContent
                className={styles.scrollableViewportContent}
                style={{ minWidth: 0 }}
              >
                {panels}
              </ScrollAreaContent>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar>
              <ScrollAreaThumb />
            </ScrollAreaScrollbar>
          </ScrollArea>
        </TabsContent>
      ) : (
        <TabsContent>{panels}</TabsContent>
      )}
    </Tabs>
  );
}

const RWCTabs = rwc(TABS_TAG_NAME, {
  component: RWCTabsComponent,
  dataSchema: tabsDataSchema,
  usesSlots: true,
  readSlotted: readTabsSlotted,
});

componentManager.register(
  RWCTabsActions,
  RWCTabsContent,
  RWCTabsList,
  RWCTabsTab,
  RWCTabsPanel,
  RWCTabs,
);
